/**
 * Die Chronik eines Angebots: Statuswechsel, Mails, Änderungen und
 * Rückmeldungen in einem Strom, neueste zuerst.
 *
 * Bewusst an einer Stelle: Die Angebotsmaske zeigt sie vollständig, „Meine
 * Aufgaben" zeigt die letzten Einträge. Zwei Umsetzungen desselben Verlaufs
 * würden auseinanderlaufen – und dann stünde in der einen Ansicht ein Eintrag,
 * den die andere nicht kennt.
 */

import { dat } from './format';
import type { Angebot, PruefLauf } from '../typen';

export type ChronikArt = 'status' | 'mail' | 'rueckmeldung' | 'aenderung' | 'pruefung';

export interface ChronikEintrag {
  id: string;
  art: ChronikArt;
  /** Sonderfall: die Anlage. Sie ist ein Statuswechsel, liest sich aber nicht so. */
  anlage: boolean;
  sortier: string;
  titel: string;
  unter: string;
  notiz: string;
  zeitText: string;
  /** Kurzform für die Zeitachse in „Meine Aufgaben": „31. Aug." */
  datumKurz: string;
  /** Wer den Eintrag verursacht hat. */
  person: string;
  /** Nur bei Mails gesetzt – führt zum Wortlaut. */
  mailId?: number;
  /**
   * Nur bei Prüfungen gesetzt – der Bericht hängt daran.
   *
   * Ein Abhaken erzeugt bewusst keinen eigenen Chronikeintrag: Bei einer Prüfung
   * mit fünf offenen Punkten wäre die Chronik danach unlesbar. Die Freigaben
   * stehen ausschließlich im Bericht der zugehörigen Prüfung.
   */
  pruefung?: PruefLauf;
}

const MONATE = [
  'Jän.', 'Feb.', 'Mär.', 'Apr.', 'Mai', 'Jun.',
  'Jul.', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.',
];

/** „2026-08-31" wird zu „31. Aug." – in einer Zeile zählt jedes Zeichen. */
export function datumKurz(iso: string): string {
  const [, monat, tag] = iso.split('-');
  const m = MONATE[Number(monat) - 1];
  return m ? `${Number(tag)}. ${m}` : iso;
}

export function chronikBauen(a: Angebot, pruefungen: PruefLauf[] = []): ChronikEintrag[] {
  return [
    /*
     * Quittierte Schritte der Bestellungen – dieselbe Optik wie Statuswechsel,
     * denn genau das sind sie: der Weg des Auftrags durch die Abwicklung.
     */
    ...a.bestellungen.flatMap((b) =>
      b.eintraege.map((e) => ({
        id: `b${e.id}`,
        art: 'status' as ChronikArt,
        anlage: false,
        sortier: `${e.datum} ${e.zeit}`,
        titel: `Bestellung ${b.nr} → ${e.schrittLang}`,
        unter: `in KORA durch ${e.user}${e.dokument ? ` · ${e.dokument}` : ''}`,
        notiz: e.notiz,
        zeitText: `${e.wochentag}, ${e.datumText} · ${e.zeit}`,
        datumKurz: datumKurz(e.datum),
        person: e.user,
      })),
    ),
    ...a.verlauf.map((v, i) => {
      const anlage = v.status === 'START' && i === 0;
      return {
        id: `s${i}`,
        art: 'status' as ChronikArt,
        anlage,
        sortier: `${v.datum} ${v.zeit || '00:00'}`,
        titel: anlage ? 'Angebot angelegt' : `Status → ${v.status}`,
        unter: anlage ? `durch ${v.user}` : `in KORA durch ${v.user}`,
        notiz: anlage ? '' : v.notiz,
        zeitText: `${v.wochentag}, ${v.datumText}${v.zeit ? ` · ${v.zeit}` : ''}`,
        datumKurz: datumKurz(v.datum),
        person: v.user,
      };
    }),
    ...a.erinnerungen.map((e) => ({
      id: `m${e.id}`,
      art: 'mail' as ChronikArt,
      mailId: e.id,
      anlage: false,
      sortier: `${e.datum} ${e.zeit}`,
      titel:
        e.art === 'auto'
          ? `${e.stufe}. Erinnerung automatisch versendet`
          : `${e.stufe}. Erinnerung ${e.artText}`,
      unter:
        `im Namen von ${e.user}` +
        (e.trockenlauf
          ? ' · Testbetrieb, nur als Entwurf abgelegt'
          : a.nachfass.antwortAm
            ? ` · Kundenantwort am ${dat(a.nachfass.antwortAm)} erkannt`
            : ' · keine Antwort erkannt'),
      notiz: '',
      zeitText: `${e.wochentag}, ${e.datumText} · ${e.zeit}`,
      datumKurz: datumKurz(e.datum),
      person: e.user,
    })),
    ...a.aenderungen.map((x) => ({
      id: `a${x.id}`,
      art: 'aenderung' as ChronikArt,
      anlage: false,
      sortier: `${x.datum} ${x.zeit}`,
      /*
       * Übersprungene Versandschritte sind kein „geänderter Wert", sondern
       * eine Entscheidung: „Ankündigung übersprungen" mit der Begründung als
       * Notiz – und „wieder fällig", wenn sie zurückgenommen wurde.
       */
      titel: x.feld.endsWith('übersprungen')
        ? x.nachher
          ? x.feld
          : `${x.feld.replace(' übersprungen', '')} wieder fällig`
        : x.feld.endsWith('zurückgenommen') || x.feld === 'Rechnung zugeordnet' ||
            x.feld.startsWith('Servicefall') ||
            /^S\d+ · /.test(x.feld)
          ? x.feld
          : x.feld === 'Anzahlung' && !x.vorher
            ? 'Anzahlung eingegangen'
            : `${x.feld} geändert`,
      unter: `in KORA durch ${x.user}`,
      // Der alte Wert gehört dazu: Ohne ihn sagt der Eintrag nicht, was geschah.
      notiz: x.feld.endsWith('übersprungen')
        ? x.nachher || `Begründung war: ${x.vorher}`
        : `${x.vorher || 'nicht gesetzt'}  →  ${x.nachher || 'entfernt'}`,
      zeitText: `${x.wochentag}, ${x.datumText} · ${x.zeit}`,
      datumKurz: datumKurz(x.datum),
      person: x.user,
    })),
    ...a.anrufe.map((c) => ({
      id: `r${c.id}`,
      art: 'rueckmeldung' as ChronikArt,
      anlage: false,
      sortier: `${c.datum} ${c.zeit}`,
      /*
       * Zwei Gespräche, eine Chronik: Die Ankündigung vor dem Versand steht
       * neben der Rückmeldung danach – nur die Überschrift unterscheidet sie.
       */
      titel:
        c.art === 'ankuendigung'
          ? `Ankündigung · ${c.ergebnisLabel}`
          : 'Rückmeldung · ' +
            (c.wiedervorlage ? `Wiedervorlage für ${dat(c.wiedervorlage)} vereinbart` : c.ergebnisLabel),
      unter:
        c.art === 'ankuendigung'
          ? `${c.kanalLabel} · erfasst von ${c.user}` +
            (c.wiedervorlage ? ` · nächster Versuch am ${dat(c.wiedervorlage)}` : '')
          : `${c.kanalLabel} · erfasst von ${c.user}` +
            (c.statusGesetzt
              ? ` · Status → ${c.statusGesetzt}`
              : c.wiedervorlage
                ? ' · Nachfassen ruhte seither'
                : ''),
      notiz: c.notiz,
      zeitText: `${c.wochentag}, ${c.datumText} · ${c.zeit}`,
      datumKurz: datumKurz(c.datum),
      person: c.user,
    })),
    ...pruefungen.map((p) => {
      const offen = p.ergebnisse.filter((e) => e.status !== 'passt' && !e.freigabeAm).length;
      const zeit = new Date(p.zeitpunkt);
      return {
        id: `p${p.id}`,
        art: 'pruefung' as ChronikArt,
        anlage: false,
        sortier: p.zeitpunkt.slice(0, 10) + ' ' + p.zeitpunkt.slice(11, 16),
        titel:
          offen === 0
            ? 'Angebot geprüft · alle Punkte erfüllt'
            : `Angebot geprüft · ${offen} ${offen === 1 ? 'Punkt braucht' : 'Punkte brauchen'} ein Urteil`,
        unter: p.checkliste
          ? `durch ${p.nutzer} · Checkliste ${p.checkliste.kategorieLabel} · ${p.checkliste.dateiname}`
          : `durch ${p.nutzer} · ohne Checkliste, Leistungsabgleich und Marge`,
        notiz: '',
        zeitText: `${zeit.toLocaleDateString('de-AT')} · ${zeit.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`,
        datumKurz: datumKurz(p.zeitpunkt.slice(0, 10)),
        person: p.nutzer,
        pruefung: p,
      };
    }),
  ].sort((x, y) => (x.sortier < y.sortier ? 1 : -1));
}